/* eslint-disable @typescript-eslint/no-use-before-define */
import { BlurView } from '@euler/components/blur-view/BlurView';
import { useBehaviorSubject } from '@euler/utils/hooks';
import * as Haptics from 'expo-haptics';
import { ImpactFeedbackStyle } from 'expo-haptics';
import {
  createContext,
  forwardRef,
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Dimensions,
  EasingFunction,
  LayoutRectangle,
  Platform,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import 'react-native-gesture-handler';
import Animated, {
  Easing,
  interpolate,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject } from 'rxjs';

// const HOLD_ITEM_TRANSFORM_DURATION = 4000;
const HOLD_ITEM_TRANSFORM_DURATION = 150;

export type HoldMenuPlacement = { margin?: number } & (
  | {
      position: 'above' | 'below';
      anchor: 'left' | 'right' | 'middle';
      align: 'left' | 'middle' | 'right';
      matchWidth?: boolean;
    }
  | {
      position: 'left' | 'right';
      anchor: 'top' | 'bottom' | 'middle';
      align: 'top' | 'middle' | 'bottom';
    }
);

export const kDefaultHoldMenuPlacement: HoldMenuPlacement = {
  position: 'below',
  anchor: 'left',
  align: 'left',
};

export type HoldItemProps = {
  children?: ReactNode;
  component: OverlayComponentProps['component'];
  placement?: HoldMenuPlacement;
  style?: StyleProp<ViewStyle>;
  backgroundMode?: 'blur' | 'transparent';
  menuSize?: { width: number; height: number };
};

export type OverlayComponentProps = {
  coordinates: LayoutRectangle;
  placement: HoldMenuPlacement;
  component: (props: {
    close: (
      callback?: () => void,
      options?: { duration?: number; easing?: EasingFunction },
    ) => void;
    replaceContent: (newContent: ReactNode) => void;
  }) => ReactElement;
  children?: ReactNode;
  background: 'blur' | 'transparent';
};

export const HoldItemContext = createContext<
  BehaviorSubject<OverlayComponentProps | undefined>
>(null as any);

export const HoldItemProvider = (props: {
  style?: StyleProp<ViewStyle>;
  children?: ReactNode;
}) => {
  const state = useRef(
    new BehaviorSubject<OverlayComponentProps | undefined>(undefined),
  ).current;
  return (
    <HoldItemContext.Provider value={state}>
      {props.children}
      <HoldItemOverlayWrapper state={state} />
    </HoldItemContext.Provider>
  );
};

export const HoldItem = forwardRef<{ present: () => void }, HoldItemProps>(
  (props, ref) => {
    const present = useContext(HoldItemContext);
    const box = useRef<View>(null);

    const presentMenu = useCallback(() => {
      box.current?.measureInWindow((x, y, width, height) => {
        Haptics.impactAsync(ImpactFeedbackStyle.Light).catch(() => null);
        present.next({
          children: props.children,
          coordinates: { x, y, width, height },
          placement: props.placement ?? kDefaultHoldMenuPlacement,
          component: props.component,
          background: props.backgroundMode ?? 'blur',
        });
      });
    }, [
      present,
      props.backgroundMode,
      props.children,
      props.component,
      props.placement,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        present: presentMenu,
      }),
      [presentMenu],
    );

    /*https://reactnative.dev/docs/view#collapsable*/
    /*Without this, the measure won't work on android*/
    return (
      <View style={props.style} collapsable={false} ref={box}>
        {props.children}
      </View>
    );
  },
);

type MenuLayoutMetrics = {
  scaleOffset: { x: number; y: number };
  position: {
    left?: number;
    top?: number;
    bottom?: number;
    right?: number;
    width: number;
    height: number;
  };
};

const HoldItemOverlay = memo(
  (
    props: OverlayComponentProps & {
      onDismiss: () => void;
    },
  ) => {
    const alpha = useSharedValue(0);
    const style = useAnimatedStyle(() => ({
      // backgroundColor: 'green',
      opacity: alpha.value,
    }));

    const pos = useSharedValue<Partial<MenuLayoutMetrics['position']>>({});

    const scaleOffset = useSharedValue<MenuLayoutMetrics['scaleOffset']>({
      x: 0,
      y: 0,
    });

    const replacementStyle = useAnimatedStyle(() => ({
      opacity: alpha.value,
    }));

    const matchWidth =
      (props.placement.position === 'below' ||
        props.placement.position === 'above') &&
      props.placement.matchWidth
        ? props.coordinates.width
        : undefined;

    const overlayStyle = useAnimatedStyle(() => ({
      opacity: alpha.value,
      left: pos.value.left,
      top: pos.value.top,
      right: pos.value.right,
      bottom: pos.value.bottom,
      width: matchWidth ?? pos.value.width,
      height: pos.value.height,
      transform: [
        {
          translateX: interpolate(
            alpha.value,
            [0, 1],
            [scaleOffset.value.x, 0],
          ),
        },
        {
          translateY: interpolate(
            alpha.value,
            [0, 1],
            [scaleOffset.value.y, 0],
          ),
        },
        {
          scale: alpha.value,
        },
        {
          translateX: interpolate(
            alpha.value,
            [0, 1],
            [-scaleOffset.value.x, 0],
          ),
        },
        {
          translateY: interpolate(
            alpha.value,
            [0, 1],
            [-scaleOffset.value.y, 0],
          ),
        },
      ],
    }));

    const dismiss = useCallback(
      (
        callback?: () => void,
        // allow the user to specify in which way to dismiss the menu
        // sometimes, we want to dismiss the menu much faster before
        // presenting the follow-up menu in a short time.
        options?: { duration?: number; easing?: EasingFunction },
      ) => {
        alpha.value = withTiming(
          0,
          {
            duration:
              options?.duration ?? 300 /* default value by reanimated */,
            // use the reanimated default in-out quad easing if not
            // specified, undefined will crash the app.
            easing: options?.easing ?? Easing.inOut(Easing.quad),
          },
          isFinished => {
            if (isFinished) {
              runOnJS(props.onDismiss)();
              if (callback) {
                runOnJS(callback)();
              }
            }
          },
        );
      },
      [alpha, props.onDismiss],
    );
    const [contentOverwrite, setContentOverwrite] = useState<ReactNode>();
    const replaceContent = useCallback(
      (content: ReactNode) => {
        const swap = () => setContentOverwrite(content);
        alpha.value = withTiming(0, {}, () => {
          runOnJS(swap)();
          alpha.value = withTiming(1);
        });
      },
      [alpha],
    );
    const { top } = useSafeAreaInsets();
    const lastLayout = useRef<LayoutRectangle>();

    const [, forceUpdate] = useState(0);

    return (
      <View
        style={StyleSheet.absoluteFill}
        css={`
          top: ${Platform.OS === 'android' ? top : 0}px;
        `}
      >
        {/** backdrop */}
        <TouchableWithoutFeedback onPress={() => dismiss()}>
          <Animated.View style={[StyleSheet.absoluteFill, style]}>
            {props.background === 'blur' ? (
              <BlurView
                style={StyleSheet.absoluteFill}
                intensity={100}
                tint="dark"
              />
            ) : (
              <View style={StyleSheet.absoluteFill} />
            )}
          </Animated.View>
        </TouchableWithoutFeedback>

        {/** clone of the hold item in blur mode */}
        {props.background === 'blur' && (
          <Animated.View
            style={[
              {
                left: props.coordinates.x,
                top: props.coordinates.y,
                width: props.coordinates.width,
                height: props.coordinates.height,
                position: 'absolute',
              },
              replacementStyle,
            ]}
          >
            {props.children}
          </Animated.View>
        )}

        {/** the menu content */}
        <Animated.View
          onLayout={event => {
            const { height, width } = event.nativeEvent.layout;
            if (
              lastLayout.current?.width == width &&
              lastLayout.current?.height === height
            ) {
              return;
            }
            lastLayout.current = event.nativeEvent.layout;
            const screenSize = Dimensions.get('window');
            const layoutMetrics = layoutMenu(
              screenSize,
              props.coordinates,
              props.placement,
              width,
              height,
              true,
            );

            scaleOffset.value = layoutMetrics.scaleOffset;
            pos.value = layoutMetrics.position;

            alpha.value = withTiming(1, {
              duration: HOLD_ITEM_TRANSFORM_DURATION,
            });

            // $note(eric): in RN 0.68 build, we have to do a force render in
            // order to the reanimated values to be updated correctly.
            forceUpdate(x => x + 1);
          }}
          style={[{ position: 'absolute' }, overlayStyle]}
        >
          {contentOverwrite ?? (
            <props.component close={dismiss} replaceContent={replaceContent} />
          )}
        </Animated.View>
      </View>
    );
  },
);

const HoldItemOverlayWrapper = memo(
  (props: { state: BehaviorSubject<OverlayComponentProps | undefined> }) => {
    const [state, setState] = useBehaviorSubject(props.state);
    if (state == null) {
      return null;
    }
    return (
      <HoldItemOverlay {...state} onDismiss={() => setState(undefined)}>
        {state.children}
      </HoldItemOverlay>
    );
  },
);

type MenuLayout = { width: number; height: number } & (
  | {
      origin: 'topLeft' | 'top';
      top: number;
      left: number;
    }
  | {
      origin: 'topRight';
      top: number;
      right: number;
    }
  | {
      origin: 'bottomLeft' | 'bottom';
      bottom: number;
      left: number;
    }
  | {
      origin: 'bottomRight';
      bottom: number;
      right: number;
    }
);

const layoutMenu = (
  screenSize: { width: number; height: number },
  targetCoords: LayoutRectangle,
  placement: HoldMenuPlacement,
  menuWidth: number,
  menuHeight: number,
  keepInBound: boolean,
): MenuLayoutMetrics => {
  const [x1, y1, x2, y2, tw, th] = [
    targetCoords.x,
    targetCoords.y,
    targetCoords.x + targetCoords.width,
    targetCoords.y + targetCoords.height,
    targetCoords.width,
    targetCoords.height,
  ];
  const [mw, mh] = [menuWidth, menuHeight];
  const margin = placement.margin ?? 5;

  // the final layout.
  // eslint-disable-next-line @typescript-eslint/init-declarations
  let layout: MenuLayout = undefined as any;
  const { position } = placement;
  if (position === 'above') {
    //#region position vertically above the target
    const { align, anchor } = placement;
    const [width, height] = [placement.matchWidth ? tw : mw, mh];

    const bottom = y1 - margin;

    if (placement.matchWidth) {
      // in match width mode, ignore anchor and align and make
      // the menu align exactly with target in middle anchor points
      layout = {
        width,
        height,
        origin: 'bottom',
        bottom,
        left: x1,
      };
    } else {
      switch (align) {
        case 'left':
          if (anchor === 'left') {
            layout = {
              width,
              height,
              origin: 'bottomLeft',
              bottom,
              left: x1,
            };
          } else if (anchor === 'middle') {
            layout = {
              width,
              height,
              origin: 'bottomLeft',
              bottom,
              left: x1 - mw / 2,
            };
          } else {
            layout = {
              width,
              height,
              origin: 'bottomRight',
              bottom,
              right: x1 - margin,
            };
          }
          break;
        case 'middle':
          if (anchor === 'left') {
            layout = {
              width,
              height,
              origin: 'bottomLeft',
              bottom,
              left: x1 + tw / 2,
            };
          } else if (anchor === 'middle') {
            layout = {
              width,
              height,
              origin: 'bottom',
              bottom,
              left: x1 + (tw - mw) / 2,
            };
          } else {
            layout = {
              width,
              height,
              origin: 'bottomRight',
              bottom,
              right: x1 + tw / 2,
            };
          }
          break;
        case 'right':
          if (anchor === 'left') {
            layout = {
              width,
              height,
              origin: 'bottomLeft',
              bottom,
              left: x2 + margin,
            };
          } else if (anchor === 'middle') {
            layout = {
              width,
              height,
              origin: 'bottomLeft',
              bottom,
              left: x2 - mw / 2,
            };
          } else {
            layout = {
              width,
              height,
              origin: 'bottomRight',
              bottom,
              right: x2,
            };
          }
          break;
      }
    }
    if (layout.bottom - layout.height < 0) {
      return layoutMenu(
        screenSize,
        targetCoords,
        {
          position: 'below',
          align,
          anchor,
        },
        mw,
        mh,
        false,
      );
    }
    //#endregion
  } else if (position === 'below') {
    //#region position vertically below the target
    const { align, anchor } = placement;

    const top = y2 + margin;

    const [width, height] = [placement.matchWidth ? tw : mw, mh];

    if (placement.matchWidth) {
      // in match width mode, ignore anchor and align and make
      // the menu align extractly with target in middle anchor points
      layout = {
        width,
        height,
        origin: 'top',
        top,
        left: x1,
      };
    } else {
      switch (align) {
        case 'left':
          if (anchor === 'left') {
            layout = {
              width,
              height,
              origin: 'topLeft',
              top,
              left: x1,
            };
          } else if (anchor === 'middle') {
            layout = {
              width,
              height,
              origin: 'topLeft',
              top,
              left: x1 - mw / 2,
            };
          } else {
            layout = {
              width,
              height,
              origin: 'topRight',
              top,
              right: x1 - margin,
            };
          }
          break;
        case 'middle':
          if (anchor === 'left') {
            layout = {
              width,
              height,
              origin: 'topLeft',
              top,
              left: x1 + tw / 2,
            };
          } else if (anchor === 'middle') {
            layout = {
              width,
              height,
              origin: 'top',
              top,
              left: x1 + (tw - mw) / 2,
            };
          } else {
            layout = {
              width,
              height,
              origin: 'topRight',
              top,
              right: x1 + tw / 2,
            };
          }
          break;
        case 'right':
          if (anchor === 'left') {
            layout = {
              width,
              height,
              origin: 'topLeft',
              top,
              left: x2 + margin,
            };
          } else if (anchor === 'middle') {
            layout = {
              width,
              height,
              origin: 'topLeft',
              top,
              left: x2 - mw / 2,
            };
          } else {
            layout = {
              width,
              height,
              origin: 'topRight',
              top,
              right: x2,
            };
          }
          break;
      }
    }
    if (layout.top + layout.height > screenSize.height && keepInBound) {
      return layoutMenu(
        screenSize,
        targetCoords,
        {
          position: 'above',
          align,
          anchor,
        },
        mw,
        mh,
        false,
      );
    }
    //#endregion
  } else if (position === 'left') {
    //#region position horizontally to the left of the target
    const { align, anchor } = placement;

    const right = x1 - margin;

    const [width, height] = [mw, mh];

    switch (align) {
      case 'top':
        if (anchor === 'top') {
          layout = {
            width,
            height,
            origin: 'topRight',
            top: y1,
            right,
          };
        } else if (anchor === 'middle') {
          layout = {
            width,
            height,
            origin: 'topRight',
            top: y1 - mh / 2,
            right,
          };
        } else {
          layout = {
            width,
            height,
            origin: 'bottomRight',
            bottom: y1 - margin,
            right,
          };
        }
        break;
      case 'middle':
        if (anchor === 'top') {
          layout = {
            width,
            height,
            origin: 'topRight',
            top: y1 + th / 2,
            right,
          };
        } else if (anchor === 'middle') {
          layout = {
            width,
            height,
            origin: 'topRight',
            top: y1 + (th - mh) / 2,
            right,
          };
        } else {
          layout = {
            width,
            height,
            origin: 'bottomRight',
            bottom: y1 + th / 2,
            right,
          };
        }
        break;
      case 'bottom':
        if (anchor === 'top') {
          layout = {
            width,
            height,
            origin: 'topRight',
            top: y2 + margin,
            right,
          };
        } else if (anchor === 'middle') {
          layout = {
            width,
            height,
            origin: 'bottomRight',
            bottom: y2 + mh / 2,
            right,
          };
        } else {
          layout = {
            width,
            height,
            origin: 'bottomRight',
            bottom: y2,
            right,
          };
        }
        break;
    }
    //#endregion
  } else if (position === 'right') {
    //#region position horizontally to the right of the target
    const { align, anchor } = placement;

    const left = x2 + margin;

    const [width, height] = [mw, mh];

    switch (align) {
      case 'top':
        if (anchor === 'top') {
          layout = {
            width,
            height,
            origin: 'topLeft',
            top: y1,
            left,
          };
        } else if (anchor === 'middle') {
          layout = {
            width,
            height,
            origin: 'topLeft',
            top: y1 - mh / 2,
            left,
          };
        } else {
          layout = {
            width,
            height,
            origin: 'bottomLeft',
            bottom: y1 - margin,
            left,
          };
        }
        break;
      case 'middle':
        if (anchor === 'top') {
          layout = {
            width,
            height,
            origin: 'topLeft',
            top: y1 + th / 2,
            left,
          };
        } else if (anchor === 'middle') {
          layout = {
            width,
            height,
            origin: 'topLeft',
            top: y1 + (th - mh) / 2,
            left,
          };
        } else {
          layout = {
            width,
            height,
            origin: 'bottomLeft',
            bottom: y1 + th / 2,
            left,
          };
        }
        break;
      case 'bottom':
        if (anchor === 'top') {
          layout = {
            width,
            height,
            origin: 'topLeft',
            top: y2 + margin,
            left,
          };
        } else if (anchor === 'middle') {
          layout = {
            width,
            height,
            origin: 'bottomLeft',
            bottom: y2 + mh / 2,
            left,
          };
        } else {
          layout = {
            width,
            height,
            origin: 'bottomLeft',
            bottom: y2,
            left,
          };
        }
        break;
    }
    //#endregion
  }

  let scaleOffset: MenuLayoutMetrics['scaleOffset'] = { x: 0, y: 0 };
  const pos: MenuLayoutMetrics['position'] = {
    width: layout.width,
    height: layout.height,
  };

  switch (layout.origin) {
    case 'topLeft':
      scaleOffset = {
        x: -layout.width / 2,
        y: -layout.height / 2,
      };
      pos.top = layout.top;
      pos.left = layout.left;
      break;
    case 'top':
      scaleOffset = { x: 0, y: -layout.height / 2 };
      pos.top = layout.top;
      pos.left = layout.left;
      break;
    case 'topRight':
      scaleOffset = {
        x: layout.width / 2,
        y: -layout.height / 2,
      };
      pos.top = layout.top;
      pos.right = layout.right;
      break;
    case 'bottomRight':
      scaleOffset = {
        x: layout.width / 2,
        y: layout.height / 2,
      };
      pos.bottom = screenSize.height - layout.bottom;
      pos.right = layout.right;
      break;
    case 'bottom':
      scaleOffset = { x: 0, y: layout.height / 2 };
      pos.bottom = screenSize.height - layout.bottom;
      pos.left = layout.left;
      break;
    case 'bottomLeft':
      scaleOffset = {
        x: -layout.width / 2,
        y: layout.height / 2,
      };
      pos.bottom = screenSize.height - layout.bottom;
      pos.left = layout.left;
      break;
  }

  return { scaleOffset, position: pos };
};
